import Raven from 'raven-js';
import {
  isEmpty,
  find,
  reject,
  get,
  omit,
  sumBy,
  isUndefined,
  differenceBy,
  isEqual,
  filter,
  groupBy,
  uniqBy,
  some,
  map,
  sortBy,
  includes,
} from 'lodash';
import { v1 as uuid } from 'uuid';
import { adjustAudienceQuotaPercentages } from 'utils/quotaHelpers';
import { QUESTION_NEST_PEEQ } from 'data/nodeTypes';
import { isOptionGroup } from 'containers/Survey/utils';
import {
  prepareValues,
  getAgeLimits,
  defaultAges,
  convertAgeGroupsToRanges,
} from './Criteria/AgeTargetingCard/helpers';

import {
  AGE_CRITERION_ID,
  GENDER_CRITERION_ID,
  GENDER_FEMALE_VALUE,
  GENDER_MALE_VALUE,
  HOUSEHOLD_INCOME_US_ID,
  usFinancialOptionGroups,
  GENERAL_TARGETING_ERROR_AUDIENCE,
} from './constants';
import { validateCriterion } from './criteriaValidator';

export function setupAudiences({
  audiences,
  countryId,
  supportsMultiCountries,
  hideAgeUnder18,
}) {
  const newAudiences = audiences.map((audience) =>
    prepareAudienceValues({
      ...audience,
      countryId: audience.countryId || countryId,
      supportsMultiCountries,
      hideAgeUnder18,
    }),
  );
  return adjustAudienceQuotaPercentages(newAudiences);
}

function prepareCriterionStyle(criterion) {
  if (isEmpty(criterion.keys)) {
    return criterion;
  }

  return {
    ...criterion,
    style: QUESTION_NEST_PEEQ,
  };
}

export function prepareCriterionByCountry({ countryId, hideAgeUnder18 }) {
  return function prepareCriterion(criterion) {
    if (criterion.key === AGE_CRITERION_ID.toString()) {
      let criterionValues = criterion.values;

      if (criterion?.groups?.length > 0) {
        criterionValues = criterion.groups.map(convertAgeGroupsToRanges);
        // eslint-disable-next-line no-param-reassign
        delete criterion.groups;
      }

      return {
        ...criterion,
        values: prepareValues(
          criterionValues,
          getAgeLimits({ countryId, hideAgeUnder18 }),
        ),
      };
    }

    return criterion;
  };
}

function getOptionGroup(group) {
  return usFinancialOptionGroups.find(
    (optionGroup) => optionGroup.text === group.name,
  );
}

function prepareAudienceValueGroupAnswers(criterion) {
  if (criterion.style === QUESTION_NEST_PEEQ || isEmpty(criterion.groups)) {
    return criterion;
  }

  const updatedCriterion = { ...criterion };
  const valuesInGroup = [];
  const groupValues = updatedCriterion.groups.map((group) => {
    const newGroup = {
      isGroup: true,
      refId: group.refId || uuid(),
      groupName: group.name,
    };

    const optionGroup = getOptionGroup(group);

    if (
      parseInt(criterion.key, 10) === HOUSEHOLD_INCOME_US_ID &&
      !!optionGroup
    ) {
      newGroup.isGroup = false;
      newGroup.isOptionGroup = true;
      newGroup.value = optionGroup.value;
    }

    if (!isUndefined(group.quotaCompletes)) {
      newGroup.quotaCompletes = group.quotaCompletes;
    }

    newGroup.values = group.values.map((value) => {
      const option = find(updatedCriterion.values, ['value', value]);
      if (isUndefined(option.quotaCompletes)) {
        valuesInGroup.push(option);
      }

      return option;
    });

    return newGroup;
  });
  const valuesNotInGroup = differenceBy(
    updatedCriterion.values,
    valuesInGroup,
    'value',
  );

  updatedCriterion.values = [...groupValues, ...valuesNotInGroup];
  delete updatedCriterion.groups;

  return updatedCriterion;
}

function prepareAudienceNesting(criterion) {
  if (criterion.style !== QUESTION_NEST_PEEQ) {
    return criterion;
  }

  const updatedCriterion = { ...criterion };

  // *** Start Code To Handle Age Groups ***
  const isNestingWithAgeGroups = updatedCriterion.groups.some(
    ({ key }) => key === AGE_CRITERION_ID.toString(),
  );
  if (isNestingWithAgeGroups) {
    updatedCriterion.groups = updatedCriterion.groups.filter(
      ({ key }) => key !== AGE_CRITERION_ID.toString(),
    );

    updatedCriterion.nestedValues = updatedCriterion.nestedValues.map(
      (nestedValue) => {
        const optionsPerFeature = groupBy(nestedValue.options, 'key');
        if (optionsPerFeature[AGE_CRITERION_ID]) {
          const { excludeOptions } = optionsPerFeature[AGE_CRITERION_ID][0];
          const ageValues = optionsPerFeature[AGE_CRITERION_ID].map(
            ({ value }) => parseInt(value, 10),
          ).sort();
          const ageValue = `${ageValues[0]} - ${
            ageValues[ageValues.length - 1]
          }`;
          optionsPerFeature[AGE_CRITERION_ID] = [
            {
              key: AGE_CRITERION_ID.toString(),
              value: ageValue,
              label: ageValue,
              excludeOptions,
            },
          ];
        }
        return {
          ...nestedValue,
          options: Object.keys(optionsPerFeature).reduce(
            (accumulator, featureKey) => [
              ...accumulator,
              ...optionsPerFeature[featureKey],
            ],
            [],
          ),
        };
      },
    );
  }
  // *** End Code To Handle Age Groups ***

  const groupsPerFeature = groupBy(updatedCriterion.groups, 'key');
  const valuesPerFeature = {};
  updatedCriterion.nestedValues.forEach((value) => {
    value.options.forEach((option) => {
      if (!valuesPerFeature[option.key]) {
        valuesPerFeature[option.key] = [];
      }
      valuesPerFeature[option.key].push(option);
    });
  });

  Object.keys(valuesPerFeature).forEach((featureKey) => {
    valuesPerFeature[featureKey] = uniqBy(
      valuesPerFeature[featureKey],
      'value',
    );
  });

  const features = updatedCriterion.keys.map((featureKey) => ({
    key: featureKey,
    excludeOptions: some(valuesPerFeature[featureKey], 'excludeOptions'),
    groups: get(groupsPerFeature, featureKey, []).map(
      ({ refId, name, values }) => ({ name, refId: refId || uuid(), values }),
    ),
    values: get(valuesPerFeature, featureKey, []).map(({ refId, value }) => ({
      refId: refId || uuid(),
      value,
    })),
  }));
  updatedCriterion.features = features.map(prepareAudienceValueGroupAnswers);
  const featuresKeys = updatedCriterion.features.map(({ key }) => key);
  updatedCriterion.key = `nest-${featuresKeys.join('-')}`;

  updatedCriterion.relations = updatedCriterion.nestedValues.map((value) => {
    const newRelation = {
      refId: value.refId || uuid(),
      features: [],
    };
    if (updatedCriterion.hasQuotas) {
      newRelation.quotaCompletes = value.quotaCompletes;
    }
    const optionsPerFeature = groupBy(value.options, 'key');
    Object.keys(optionsPerFeature).forEach((featureKey) => {
      const relationFeature = { key: featureKey };
      const feature = find(updatedCriterion.features, ['key', featureKey]);
      if (optionsPerFeature[featureKey].length > 1) {
        const sorted = sortBy(map(optionsPerFeature[featureKey], 'value'));
        const group = find(feature.values, ({ values }) =>
          isEqual(sortBy(map(values, 'value')), sorted),
        );
        relationFeature.featureValueRefId = group.refId;
      } else {
        const matchingValue = get(
          optionsPerFeature,
          `[${featureKey}][0].value`,
        );
        const option = feature.values.find((item) => {
          if (item.isGroup || isOptionGroup(item)) {
            return item.values.some(
              (groupItem) => groupItem.value === matchingValue,
            );
          }

          return item.value === matchingValue;
        });

        relationFeature.featureValueRefId = option.refId;
      }
      newRelation.features.push(relationFeature);
    });

    return newRelation;
  });

  return omit(updatedCriterion, ['keys', 'groups', 'nestedValues']);
}

function prepareAudienceValues({
  id,
  refId,
  quotaCompletes,
  quotaName,
  features = [],
  externalAudienceId,
  supported = true,
  isActive,
  timestamp,
  countryId,
  hideAgeUnder18,
  countryChangeAllowed,
  supportsMultiCountries,
}) {
  const criteria = features
    .map(prepareCriterionStyle)
    .map(prepareCriterionByCountry({ countryId, hideAgeUnder18 }))
    .map(prepareAudienceValueGroupAnswers)
    .map(prepareAudienceNesting);

  const values = {
    id,
    refId,
    quotaCompletes,
    quotaName,
    criteria,
    externalAudienceId,
    supported,
    isActive,
    timestamp,
    countryChangeAllowed,
  };

  if (supportsMultiCountries) {
    values.countryId = countryId;
  }

  return values;
}

function setupAudiencesOnSuccess({
  allAudiences,
  state,
  completes = Infinity,
  completed = 0,
  surveyConfig = {},
  countryId,
  hideAgeUnder18,
}) {
  const audiences = setupAudiences({
    audiences: allAudiences,
    countryId,
    supportsMultiCountries: surveyConfig.allowMultipleCountries,
    hideAgeUnder18,
  });
  const newErrors = handleInvalidPeeqAudienceErrors({
    audiences,
    errors: state.errors,
    completes,
    completed,
    skipTotalCompletesErrorCheck: surveyConfig.managedTargeting,
  });

  return {
    audiences,
    activeAudienceRefId: audiences[0]?.refId,
    errors: newErrors,
  };
}

function fetchTargetingSuccessActionHandler(state, payload) {
  const currentVersion = state?.targetingEditVersion || -1;
  const receivedVersion = payload?.targetingEditVersion || -1;

  // coming here means that we should not accept what the server gave us, but re-submit what we already have
  // forceAcceptServerVersion bypasses this after discard changes (reloadSurveyState with afterClearRevision)
  if (!payload?.forceAcceptServerVersion && currentVersion > receivedVersion) {
    Raven.captureMessage(
      '[Targeting]: Received version previous than already stored',
    );
    return Object.assign({}, state, {
      isFetching: false,
    });
  }

  const {
    audiences = [],
    countryId,
    targetingEditVersion = 1,
    startVersion = 1,
    previousStatusName,
    completes,
    completed = 0,
    surveyConfig,
    hideAgeUnder18,
    hasDifferencesInAudiences = false,
  } = payload;

  return Object.assign({}, state, {
    isFetching: false,
    hasFetched: true,
    startVersion,
    previousStatusName,
    targetingEditVersion,
    savedTargetingVersion: targetingEditVersion,
    countryId,
    hasDifferencesInAudiences,
    ...setupAudiencesOnSuccess({
      allAudiences: audiences,
      state,
      completes,
      completed,
      surveyConfig,
      countryId,
      hideAgeUnder18,
    }),
  });
}

function getAllCriteriaArray(criteria) {
  return Object.keys(criteria).reduce(
    (accumulator, key) => [...accumulator, ...criteria[key]],
    [],
  );
}

function clearOutdatedErrors(errors, audiences) {
  const allCriteria = audiences.reduce(
    (accumulator, audience) => [...accumulator, ...audience.criteria],
    [],
  );

  return errors.filter((error) =>
    allCriteria.some(({ key }) => error.key === key),
  );
}

function clearAudienceFromDeletedCriteria(newCriteria) {
  const allCriteria = getAllCriteriaArray(newCriteria);

  return (audience) => {
    if (!audience.supported) return audience;
    const clearedCriteria = audience.criteria.reduce(
      (accumulator, criterion) => {
        const foundCriterion = allCriteria.find(
          (item) => item.key === criterion.key,
        );
        if (foundCriterion && !foundCriterion.isDeleted) {
          return [...accumulator, criterion];
        }

        return accumulator;
      },
      [],
    );

    return {
      ...audience,
      criteria: clearedCriteria,
    };
  };
}

function mapPeeqCriterion(criterion) {
  return {
    ...criterion,
    key: criterion.id.toString(),
    options: criterion.options.map((option) => ({
      ...option,
      text: option.displayText || option.text,
      value: option.id.toString(),
    })),
  };
}

function mapPeeqCriteria(criteria) {
  return Object.keys(criteria).reduce((accumulator, key) => {
    accumulator[key] = criteria[key].map(mapPeeqCriterion);

    return accumulator;
  }, {});
}

function handleInvalidPeeqAudienceErrors({
  audiences = [],
  errors = [],
  completes = Infinity,
  completed = 0,
  skipTotalCompletesErrorCheck = true,
}) {
  const generalAudienceError = {
    category: 'audience',
    key: 'none',
    audienceRefId: GENERAL_TARGETING_ERROR_AUDIENCE,
  };

  let emptyAudienceRefId;
  const hasEmptyAudience = audiences
    .filter(({ supported }) => Boolean(supported))
    .some(({ refId, criteria = [] }) => {
      const isEmptyAudience = criteria.every(
        ({ style, features = [], values = [] }) => {
          if (style === QUESTION_NEST_PEEQ) {
            return features.every(
              ({ values: featureValues }) => featureValues.length === 0,
            );
          }
          return values.length === 0;
        },
      );
      if (isEmptyAudience) {
        emptyAudienceRefId = refId;
      }

      return isEmptyAudience;
    });

  const emptyValuesCriteriaErrors = [];
  audiences.forEach(({ criteria, quotaCompletes, refId }) => {
    criteria.forEach((criterion) => {
      const criterionError = validateCriterion(criterion, {
        completes: quotaCompletes,
      });
      if (!isEmpty(criterionError)) {
        emptyValuesCriteriaErrors.push({
          key: criterion.key,
          errors: criterionError,
          category: 'criteria',
          audienceRefId: refId,
        });
      }
    });
  });
  const existingGeneralErrors = find(errors, generalAudienceError);

  const updatedErrors = [
    existingGeneralErrors,
    ...emptyValuesCriteriaErrors,
  ].filter(Boolean);
  const audienceResponsesSum = sumBy(audiences, 'quotaCompletes');
  const quotaSumError = audienceResponsesSum + completed < completes;

  if (!existingGeneralErrors && !hasEmptyAudience && !quotaSumError) {
    return updatedErrors;
  }

  const errorsToOmit = [];
  if (!skipTotalCompletesErrorCheck) {
    errorsToOmit.push('quotaSumError');
  }

  const actualGeneralErrorCodes = omit(
    get(existingGeneralErrors, 'errors', {}),
    errorsToOmit,
  );
  const restOfErrors = reject(
    reject(updatedErrors, generalAudienceError),
    'errors.hasEmptyAudience',
  );

  if (hasEmptyAudience) {
    restOfErrors.push({
      category: 'audience',
      key: 'none',
      audienceRefId: emptyAudienceRefId,
      errors: { hasEmptyAudience: true },
    });
  }
  if (!skipTotalCompletesErrorCheck && quotaSumError && audiences.length > 0) {
    actualGeneralErrorCodes.quotaSumError = true;
  }

  if (isEmpty(actualGeneralErrorCodes)) {
    return restOfErrors;
  }

  return restOfErrors.concat(
    Object.assign({}, generalAudienceError, {
      errors: actualGeneralErrorCodes,
    }),
  );
}

function removeErrorsOfCriterion({
  errors,
  demographicKey,
  audienceRefIdToRemove,
}) {
  return filter(
    errors,
    ({ key, audienceRefId }) =>
      !(key === demographicKey && audienceRefId === audienceRefIdToRemove),
  );
}

function generateTargetingCriterion({
  allCriteriaUnGrouped,
  criteria,
  countryId,
  targetingVersion,
  extraCriterionProps = {},
}) {
  const newCriteria = criteria.map((criterionKey, index) => {
    const criterion = find(allCriteriaUnGrouped, ['key', criterionKey]);
    let values = [];
    if (criterion.questionType === 'Numeric') {
      values = defaultAges(countryId);
      // Update version to trigger a store action automatically
      // Only needed for numeric types because they are the only ones
      // for which we add values automatically at creation
      // eslint-disable-next-line no-param-reassign
      targetingVersion += 1;
    }

    return {
      key: criterionKey,
      style: criterion.questionType,
      values,
      hasQuotas: false,
      scrollIntoView: index === 0 ? Date.now() : undefined,
      ...extraCriterionProps,
    };
  });

  return {
    criteria: newCriteria,
    targetingVersion,
  };
}

function addTargetingCriterion({
  audiences,
  errors,
  countryId,
  mainCriteria,
  customQuestions,
  audienceRefId,
  criteria,
  targetingVersion,
  supportsMultiCountries,
  extraCriterionProps = {},
}) {
  const allCriteriaUnGrouped = Object.values({
    ...mainCriteria,
    ...customQuestions,
  }).flat();
  let updatedTargetingEditVersion = targetingVersion;
  const updatedAudiences = audiences.map((audience) => {
    if (audience.refId !== audienceRefId) {
      return audience;
    }
    let newCriteria;
    ({ criteria: newCriteria, targetingVersion: updatedTargetingEditVersion } =
      generateTargetingCriterion({
        allCriteriaUnGrouped,
        criteria,
        countryId: supportsMultiCountries ? audience.countryId : countryId,
        targetingVersion,
        extraCriterionProps,
      }));

    return {
      ...audience,
      criteria: [...audience.criteria, ...newCriteria],
    };
  });
  const newErrors = handleInvalidPeeqAudienceErrors({
    audiences: updatedAudiences,
    errors,
  });

  return {
    audiences: updatedAudiences,
    errors: newErrors,
    targetingVersion: updatedTargetingEditVersion,
  };
}

function removeTargetingCriterion({
  audiences,
  errors,
  audienceRefIdToRemove,
  demographicKey,
}) {
  const updatedAudiences = audiences.map((audience) => {
    if (audience.refId !== audienceRefIdToRemove) {
      return audience;
    }

    return {
      ...audience,
      criteria: audience.criteria.filter(
        (criterion) => criterion.key !== demographicKey,
      ),
    };
  });
  let newErrors = removeErrorsOfCriterion({
    errors,
    demographicKey,
    audienceRefIdToRemove,
  });
  newErrors = handleInvalidPeeqAudienceErrors({
    audiences: updatedAudiences,
    errors: newErrors,
  });

  return {
    audiences: updatedAudiences,
    errors: newErrors,
  };
}

function enrichGenderCriterion(criterion) {
  if (criterion.key !== GENDER_CRITERION_ID.toString()) {
    return criterion;
  }
  return {
    ...criterion,
    values: [
      { value: GENDER_MALE_VALUE, refId: uuid() },
      { value: GENDER_FEMALE_VALUE, refId: uuid() },
    ],
  };
}

function ensureAgeAndGenderCriterionExistOnNestingRemove({
  mainCriteria,
  customQuestions,
  countryId,
  audienceCriteria,
  updatedTargetingEditVersion,
  surveyConfig,
}) {
  if (!surveyConfig.managedTargeting) {
    const missingCriteria = {
      [AGE_CRITERION_ID]: true,
      [GENDER_CRITERION_ID]: true,
    };
    const missingKeys = Object.keys(missingCriteria);

    audienceCriteria.forEach((criterion) => {
      if (criterion.style === QUESTION_NEST_PEEQ) {
        missingKeys.forEach((missingCriterion) => {
          const found = criterion.features.some(
            ({ key }) => key === missingCriterion,
          );
          if (found) {
            missingCriteria[missingCriterion] = false;
          }
        });
      } else {
        const found = includes(missingKeys, criterion.key);
        if (found) {
          missingCriteria[criterion.key] = false;
        }
      }
    });

    const missingCriteriaKeys = Object.keys(missingCriteria).filter(
      (key) => missingCriteria[key],
    );

    if (!isEmpty(missingCriteriaKeys)) {
      const allCriteriaUnGrouped = Object.values({
        ...mainCriteria,
        ...customQuestions,
      }).flat();

      let { criteria: newCriteria } = generateTargetingCriterion({
        allCriteriaUnGrouped,
        criteria: missingCriteriaKeys,
        countryId,
        targetingVersion: updatedTargetingEditVersion,
        extraCriterionProps: {
          isPristine: true,
        },
      });
      newCriteria = newCriteria.map(enrichGenderCriterion);

      return [...audienceCriteria, ...newCriteria];
    }
  }

  return audienceCriteria;
}

/**
 * Returns hardcoded grouped options only if the provided options match the following ids: ^(?:[1-9]|1[0-9]|2[0-8]|30)$
 * @param {Array} options
 * @returns Returns options groups.
 */
function getUsHouseholdIncomeOptionGroups(options) {
  if (!options || !Array.isArray(options)) return null;

  const regex = /^(?:[1-9]|1[0-9]|2[0-8]|30)$/;

  const isValid = options.every((option) => regex.test(option.id));

  if (!isValid) {
    Raven.captureException('GET-OPTION-GROUPS-ERROR', {
      extra: {
        optionGroups: options,
      },
    });

    return null;
  }

  return usFinancialOptionGroups;
}

export {
  fetchTargetingSuccessActionHandler,
  clearOutdatedErrors,
  clearAudienceFromDeletedCriteria,
  mapPeeqCriteria,
  prepareAudienceValues,
  handleInvalidPeeqAudienceErrors,
  mapPeeqCriterion,
  getAllCriteriaArray,
  addTargetingCriterion,
  removeTargetingCriterion,
  generateTargetingCriterion,
  enrichGenderCriterion,
  removeErrorsOfCriterion,
  ensureAgeAndGenderCriterionExistOnNestingRemove,
  prepareAudienceNesting,
  getUsHouseholdIncomeOptionGroups,
};
